/**
 * Staged bulk pass-upload job (YB-1669).
 *
 * Mirrors PAPI's pass_upload_jobs registry row: create_event/update_event with a
 * tickets_url no longer writes passes inline — the excel is parsed + validated and
 * `queued` immediately (no confirm gate); the PAPI worker drains it chunk-by-chunk.
 * The portal tracks progress in the navbar bell panel and shows invalid rows in the
 * errors dialog (`awaiting_review`/confirm remain in the enum for the API state
 * machine, but portal uploads never enter them).
 *
 * The bell is restored from the API on every app load — including recently FINISHED
 * jobs — so a user who closed the browser mid-upload still finds the result (and the
 * error file) waiting. `dismissed_at` is what takes a job off the list, permanently.
 */
export type PassUploadJobStatus =
  | 'validating'
  | 'awaiting_review'
  | 'queued'
  | 'processing'
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'canceled'
  | 'expired'

/**
 * Which tool produced a run (YB-2120). `pass_upload` is every job written before
 * Service Utilities existed, and every one written since by a path that does not name
 * a type — so a missing value means an excel upload, never "unknown".
 */
export type ServiceJobType = 'pass_upload' | 'bulk_area_update' | 'asset_import'

/**
 * The PASS_UPLOADS keys whose wording depends on what the job DID. An excel upload's
 * errors note ("the valid rows are being uploaded… re-upload ONLY those rows") is wrong
 * for an area run and for an all-or-nothing vehicle import, so each non-upload type has
 * its own copy of these under PASS_UPLOADS.TYPES.<JOB_TYPE>. Every other key is shared.
 */
export type TypedPassUploadKey = 'JOB' | 'ERRORS_TITLE' | 'ERRORS_NOTE' | 'ERRORS_NOTE_DONE' | 'VALID_ROWS' | 'INVALID_ROWS' | 'COMPLETED_WITH_ERRORS_NOTE'

/** The translation key for `key` as it applies to this job's type. */
export function passUploadTextKey(jobType: ServiceJobType | string | null | undefined, key: TypedPassUploadKey): string {
  const type = jobType || 'pass_upload'
  return type === 'pass_upload' ? `PASS_UPLOADS.${key}` : `PASS_UPLOADS.TYPES.${type.toUpperCase()}.${key}`
}

export class PassUploadJob {
  id: number = NaN
  client_id: number = NaN
  source: 'portal_excel' | 'external_api' = 'portal_excel'
  job_type: ServiceJobType = 'pass_upload'
  status: PassUploadJobStatus = 'validating'
  event_id: number | null = null
  total_rows: number = 0
  valid_rows: number = 0
  invalid_rows: number = 0
  committed_rows: number = 0
  committed_offset: number = 0
  attempts: number = 0
  /** Who started a run that has no confirm step (a Service Utilities tool), or the external system's name. */
  system_name: string | null = null
  confirmed_by: string | null = null
  confirmed_at: string | null = null
  expires_at: string | null = null
  last_error: string | null = null
  /** Set once the user dismissed the job from the bell (server-side, so it survives a reload). */
  dismissed_at: string | null = null
  created_at: string = ''
  updated_at: string = ''
}

/** Terminal states — polling stops on these. */
export const PASS_UPLOAD_TERMINAL_STATUSES: PassUploadJobStatus[] = ['completed', 'completed_with_errors', 'failed', 'canceled', 'expired']
